// helpers module
// functions to be used by vue components
import moment from "moment";


// ----------------------------------------------------------------------------
export default {
  // ----------------------------------------------------------------------------
  // Function that creates an attribute field for a form
    create_attributes_field(tmp_category_attribute, selected_category) {
    const list = Array.isArray(tmp_category_attribute) ? tmp_category_attribute : [];
    const sorted = [...list].sort((a, b) => {
      const levelDiff = (a.level ?? 999) - (b.level ?? 999);
      if (levelDiff !== 0) return levelDiff;
      const sortDiff = (a.sort_nr ?? 999) - (b.sort_nr ?? 999);
      if (sortDiff !== 0) return sortDiff;
      return (a.attribute_name || "").localeCompare(b.attribute_name || "");
    });

    const selectedLeafId =
      typeof selected_category === "string"
        ? selected_category
        : selected_category && typeof selected_category === "object"
          ? Object.keys(selected_category)[0]
          : null;

    let options = {};
    const orderedIds = [];
    sorted.map((d) => {
      const isRequired = !!d.is_required;
      const effectiveType = d.input_type_override || d.type_id;
      const owningCategoryId = d.guid || selectedLeafId;
      const parsedValue = this.parseAttributeFieldValue(d.attribute_value ?? "", effectiveType);
      if (!options[d.attribute_id]) {
        orderedIds.push(d.attribute_id);
        options[d.attribute_id] = {
          root: {
            guid: d.attribute_id,
            name: d.attribute_name,
            type: effectiveType,
            dependency: owningCategoryId,
            category_id: owningCategoryId,
            value: parsedValue,
            is_required: isRequired,
            measuring_unit: d.measuring_unit ?? null,
            default_measuring_unit: d.default_measuring_unit ?? null,
            measuring_unit_name: d.measuring_unit_name ?? null,
          },
          children: [],
        };
      } else {
        options[d.attribute_id].root.is_required = options[d.attribute_id].root.is_required || isRequired;
        if (this.isAttributeRootValueEmpty(options[d.attribute_id].root) && !this.isAttributeRootValueEmpty({ type: effectiveType, value: parsedValue })) {
          options[d.attribute_id].root.value = parsedValue;
        }
        if (d.measuring_unit != null && options[d.attribute_id].root.measuring_unit == null) {
          options[d.attribute_id].root.measuring_unit = d.measuring_unit;
        }
        if (d.default_measuring_unit != null && options[d.attribute_id].root.default_measuring_unit == null) {
          options[d.attribute_id].root.default_measuring_unit = d.default_measuring_unit;
        }
      }
      if (d.child_attribute_guid && !options[d.attribute_id].children.some((c) => c.guid === d.child_attribute_guid)) {
        options[d.attribute_id].children.push({
          guid: d.child_attribute_guid,
          name: d.child_attribute_name,
        });
      }
    });

    const ordered = {};
    for (const id of orderedIds) {
      const opt = options[id];
      if (opt?.root?.type === "select") {
        opt.root.value = this.resolveSelectValueToOptionGuid(opt.root.value, opt.children);
        this.ensureSelectValueInChildren(opt);
      } else if (opt?.root?.type === "multiselect") {
        const guids = this.normalizeMultiselectAttributeValue(opt.root.value).map((g) =>
          this.resolveSelectValueToOptionGuid(g, opt.children)
        );
        opt.root.value = guids.filter(Boolean);
        this.ensureMultiselectValuesInChildren(opt);
      }
      ordered[id] = opt;
    }
    return ordered;
  },

  parseAttributeFieldValue(rawValue, effectiveType) {
    if (effectiveType === "multiselect") {
      if (Array.isArray(rawValue)) {
        return rawValue;
      }
      if (typeof rawValue === "string" && rawValue !== "") {
        try {
          const parsed = JSON.parse(rawValue);
          return Array.isArray(parsed) ? parsed : [];
        } catch {
          return [];
        }
      }
      return [];
    }
    if (effectiveType === "checkbox") {
      return this.normalizeCheckboxValue(rawValue);
    }
    return rawValue ?? "";
  },

  ensureSelectValueInChildren(opt) {
    const v = String(opt?.root?.value ?? "").trim();
    if (!v) return;
    if ((opt.children || []).some((c) => c.guid === v)) return;
    opt.children = [...(opt.children || []), { guid: v, name: v }];
  },

  ensureMultiselectValuesInChildren(opt) {
    const guids = this.normalizeMultiselectAttributeValue(opt?.root?.value);
    const children = opt.children || [];
    const extra = [];
    for (const g of guids) {
      if (!g) continue;
      if (children.some((c) => c.guid === g) || extra.some((c) => c.guid === g)) continue;
      extra.push({ guid: g, name: g });
    }
    if (extra.length) opt.children = [...children, ...extra];
  },

  normalizeCheckboxValue(value) {
    const v = String(value ?? "").trim().toLowerCase();
    if (v === "") return "";
    if (["1", "true", "po", "yes"].includes(v)) return "1";
    if (["0", "false", "jo", "no"].includes(v)) return "0";
    return String(value ?? "").trim();
  },

  resolveSelectValueToOptionGuid(value, children) {
    const raw = String(value ?? "").trim();
    if (!raw) return "";
    const list = children || [];
    if (list.some((c) => c.guid === raw)) return raw;
    const byName = list.find((c) => String(c.name || "").trim().toLowerCase() === raw.toLowerCase());
    return byName?.guid || raw;
  },

  /**
   * When category attributes reload without entity values, keep previously entered values.
   */
  mergePreservedAttributeValues(newOptions, oldOptions) {
    if (!newOptions || !oldOptions) return newOptions;
    for (const id of Object.keys(newOptions)) {
      const newRoot = newOptions[id]?.root;
      const oldRoot = oldOptions[id]?.root;
      if (!newRoot || !oldRoot) continue;
      if (!this.isAttributeRootValueEmpty(newRoot)) continue;
      if (this.isAttributeRootValueEmpty(oldRoot)) continue;

      if (newRoot.type === "select") {
        const resolved = this.resolveSelectValueToOptionGuid(oldRoot.value, newOptions[id].children);
        const childGuids = new Set((newOptions[id].children || []).map((c) => c.guid));
        if (childGuids.has(resolved)) {
          newRoot.value = resolved;
        } else {
          const oldChild = (oldOptions[id].children || []).find((c) => c.guid === oldRoot.value);
          if (oldChild) {
            const match = (newOptions[id].children || []).find((c) => c.name === oldChild.name);
            if (match) newRoot.value = match.guid;
          }
        }
      } else if (newRoot.type === "multiselect") {
        const oldGuids = this.normalizeMultiselectAttributeValue(oldRoot.value);
        const mapped = oldGuids
          .map((g) => this.resolveSelectValueToOptionGuid(g, newOptions[id].children))
          .filter((g) => (newOptions[id].children || []).some((c) => c.guid === g));
        if (mapped.length) newRoot.value = mapped;
      } else if (newRoot.type === "checkbox") {
        newRoot.value = this.normalizeCheckboxValue(oldRoot.value);
      } else {
        newRoot.value = oldRoot.value;
      }
    }
    return newOptions;
  },

  normalizeMultiselectAttributeValue(value) {
    if (Array.isArray(value)) return value;
    if (typeof value === "string" && value !== "") {
      try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
    return [];
  },

  isAttributeRootValueEmpty(root) {
    const v = root?.value;
    if (v === null || v === undefined) return true;
    if (root.type === "multiselect") {
      return this.normalizeMultiselectAttributeValue(v).length === 0;
    }
    if (["text", "number", "textarea"].includes(root.type)) return String(v).trim() === "";
    if (root.type === "checkbox") return !["0", "1"].includes(String(v));
    return String(v).trim() === "";
  },

  markAllAttributesRequired(options, required = true) {
    if (!options || typeof options !== "object") return options;
    Object.keys(options).forEach((key) => {
      if (options[key]?.root) options[key].root.is_required = !!required;
    });
    return options;
  },

  mergeAttributeOptionsFromSource(targetOptions, sourceOptions) {
    const merged = JSON.parse(JSON.stringify(targetOptions || {}));
    let copiedCount = 0;
    let skippedSelectCount = 0;

    for (const id of Object.keys(merged)) {
      const source = sourceOptions?.[id];
      if (!source?.root) continue;

      const targetRoot = merged[id].root;
      const sourceRoot = source.root;

      if (this.isAttributeRootValueEmpty(sourceRoot)) continue;

      const type = targetRoot.type;
      const childGuids = new Set((merged[id].children || []).map((c) => c.guid));

      if (type === "select") {
        const sourceValue = this.resolveSelectValueToOptionGuid(
          String(sourceRoot.value ?? "").trim(),
          merged[id].children || []
        );
        if (!childGuids.has(sourceValue)) {
          skippedSelectCount++;
          continue;
        }
        targetRoot.value = sourceValue;
      } else if (type === "multiselect") {
        const sourceGuids = this.normalizeMultiselectAttributeValue(sourceRoot.value)
          .map((g) => this.resolveSelectValueToOptionGuid(g, merged[id].children || []))
          .filter((g) => childGuids.has(g));
        if (sourceGuids.length === 0) {
          skippedSelectCount++;
          continue;
        }
        targetRoot.value = [...sourceGuids];
      } else if (type === "checkbox") {
        targetRoot.value = this.normalizeCheckboxValue(sourceRoot.value);
      } else {
        targetRoot.value = sourceRoot.value;
      }

      if (sourceRoot.measuring_unit != null) {
        targetRoot.measuring_unit = sourceRoot.measuring_unit;
      }

      copiedCount++;
    }

    return { merged, copiedCount, skippedSelectCount };
  },

  // Function that creates a tree structure form a flat array
  toTree(dataset, identity) {
    const hashTable = Object.create(null);
    dataset.forEach((aData) => (hashTable[aData[identity]] = { ...aData }));
    const dataTree = [];
    dataset.forEach((aData) => {
      if (aData.parent_id) {
        if(hashTable[aData.parent_id]){
          if (!hashTable[aData.parent_id].children)
          hashTable[aData.parent_id].children = [];
          hashTable[aData.parent_id].children.push(hashTable[aData[identity]]);
          
          // if there is a sort_nr field sort the children by it
          if(hashTable[aData[identity]].sort_nr){
            hashTable[aData.parent_id].children.sort((a, b) => {
              return a.sort_nr - b.sort_nr;
            });
          }
        }
      } else dataTree.push(hashTable[aData.guid]);
    });
    return dataTree;
  },

  dateFormatter(date) {
    return this.formatAuditDateTime(date?.value);
  },

  // MySQL/Laravel datetimes are stored in UTC without a zone suffix.
  // Parse as UTC then show the user's local clock (e.g. Europe/Tirane).
  formatAuditDateTime(d) {
    if (!d) return "";
    const m = moment.utc(d).local();
    return m.isValid() ? m.format("DD/MM/YYYY HH:mm") : "";
  },

  timeFormatter(date) {
    return moment(date.value).format("HH:mm");
  },
  
  // Print module
  get_html_table(product_table_columns, items, total_row){
    var replaced_table_chars = this.replace_table_chars_and_split(product_table_columns)
    var table = "<style>" +
    " .table-html .totals_column_name { font-size: 10px; text-align: left; text-transform: uppercase; font-weight: normal; }" + 
    " .table-html .right { text-align: right}" + 
    " .table-html .left { text-align: left}" + 
    " .table-html td, th { border: none !important; border-bottom: 0.01em solid #ccc !important;} " + 
    "</style>" 
    table += "<table class='table-html table' style='width:100%;'>"

    table += "<thead><tr>"
    for (let i = 0; i < replaced_table_chars.length; i++) {
      var sp = replaced_table_chars[i]
      var replaced_chars1 = this.replace_chars_and_split(sp)

      if(replaced_chars1[2]){
        var r = new RegExp("_", "g")
      }
      table += "<th class='left' style='" + (replaced_chars1[2]?.replace(r, ": ") ?? '') + "'>" + replaced_chars1[1] + "</th>"
    }
    table += "</tr></thead>"

    table += "<tbody>"
    var totals = {}
    // var price_total = 0
    // var qty_total = 0
    for (var j = 0; j < items.length; j++) {
      table += "<tr> "
      // if(replaced_table_chars.length > 0 && (replaced_table_chars[0] != '' && replaced_table_chars[0] != undefined)){
        for (var k = 0; k < replaced_table_chars.length; k++) {
            var sp1 = replaced_table_chars[k]
            var replaced_chars = this.replace_chars_and_split(sp1)
            if(replaced_chars[2]){
              var r1 = new RegExp("_", "g")
            }
            table += "<td style='" + (replaced_chars[2]?.replace(r1, ': ') ?? '') + "'>" + (items[j][replaced_chars[0]] ?? '<i>-</i>') + "</td>"
            if(replaced_chars.length > 3 ){
                if(replaced_chars[3] == '*'){
                  if(!totals[replaced_chars[0]]) totals[replaced_chars[0]] = {total: 0, name: replaced_chars[1]}
                  totals[replaced_chars[0]].total += parseFloat(items[j][replaced_chars[0]])
                }
            }
        }
      // }
        
        table += "</tr>"
    }

    table += "</tbody>"

    if(Object.keys(totals).length > 0){
      if(total_row){
        table +="<tfoot>"
        var iii = 0 
        Object.keys(totals).map(key => {
          table += "<tr>"
          if(iii == 0) table += "<th valign='top' class='left' colspan='"+(replaced_table_chars.length - 2)+"' rowspan='"+(Object.keys(totals).length) + "'>TOTAL</th>"
          table += "<th class='totals_column_name'>"+(totals[key].name ?? '')+"</th>"
          table += "<th class='right has-text-right'>"+(totals[key].total ?? '')+"</th>"
          table += "</tr>"
          iii++

        })
        table +="</tfoot>"
      } else {
        table += "<tfoot><tr>"
        for (let i = 0; i < replaced_table_chars.length; i++) {
          var sp2 = replaced_table_chars[i]
          var replaced_chars2 = this.replace_chars_and_split(sp2)
          if(i == 0) { 
            table += "<th class='left'>TOTAL</th>"
          }
          else table += "<th class='right'>" + (totals[replaced_chars2[0]] ? totals[replaced_chars2[0]].total : '') + "</th>"
        }
        if(!total_row) table += "</tr>"
        table += "</tr></tfoot>"
      }
    }


    table += "</table>"

    return table
  },
  replace_table_chars_and_split(str) {
      str = str.replace("[", "")
      str = str.replace("]", "")
      str = str.replace("' , ", "',")
      str = str.replace("' ,", "',")
      str = str.replace("', ", "',")
      str = str.split(",")
      return str
  },
  replace_chars_and_split(str){ 
    // str = str.replace("\n", "")
    var regex = new RegExp("'", 'g')
    str = str.replace(regex, '')
    var regex1 = new RegExp('\\n', 'g')
    str = str.replace(regex1, '')
    str = str.replace(" ", '')
    str = str.split(":")

    return str
  },
};
